import { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import { com } from '@eidu/web'
import { usePublishEntity } from '../../../../../io/useRegistryAsset'
import { useSnackBar } from '../../../../../components/feedback/SnackBarProvider'
import EntityId = com.eidu.sharedlib.entity.EntityId

type PublishDialogProps = {
  entityId: EntityId
  inlineDependencyCount?: number
}

const PublishDialog = ({ entityId, inlineDependencyCount }: PublishDialogProps) => {
  const [open, setOpen] = useState(false)
  const [minAppVersion, setMinAppVersion] = useState('')
  const { showSnackBar } = useSnackBar()
  const { publish, isPending: publishing } = usePublishEntity()

  const onConfirm = async () => {
    try {
      const compatibility: Record<string, string> = minAppVersion.trim() ? { app_version: minAppVersion.trim() } : {}
      const result = await publish({ id: entityId, compatibility })
      showSnackBar(`Published v${result.versionId.substring(0, 8)}…`, 'success')
      setOpen(false)
      setMinAppVersion('')
    } catch (error) {
      showSnackBar(`Publish failed: ${(error as Error).message}`, 'error')
    }
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}>Publish</Button>
      <Dialog open={open} onClose={() => !publishing && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Publish asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DialogContentText>
              Publishing creates an immutable snapshot of this asset and its dependencies, appended to the asset&apos;s
              versioned log. Field Apps with the right permission will pick up the new version on their next sync.
            </DialogContentText>
            {inlineDependencyCount !== undefined && inlineDependencyCount > 0 && (
              <Alert severity="warning">
                This asset has {inlineDependencyCount} non-published{' '}
                {inlineDependencyCount === 1 ? 'dependency' : 'dependencies'}. They will be embedded as-is and frozen at
                this version.
              </Alert>
            )}
            <TextField
              label="Minimum app version (optional)"
              helperText="Field App versions older than this will ignore this publish."
              value={minAppVersion}
              onChange={(e) => setMinAppVersion(e.target.value)}
              placeholder="e.g. 3.15.0"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={publishing}>
            Cancel
          </Button>
          <Button onClick={onConfirm} variant="contained" disabled={publishing}>
            {publishing ? 'Publishing…' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PublishDialog
